/**
 * Shipping Category Calculator for Shopify Cart
 * Determines shipping category based on product collections
 * Version: 9.0 - Cost-based shipping categories (SHIPPING_[cost])
 */

(function() {
  'use strict';

  // ========== CONFIGURATION ==========

  /** Prefix on every shipping_category value. Checkout rule "does not contain CAT_" then matches only when attribute is missing (e.g. Apple Pay). */
  const SHIPPING_CATEGORY_PREFIX = 'CAT_';

  /** Auto-added by small-order-surcharge.js — must never count as a (un)categorized cart item. */
  const SURCHARGE_HANDLE = 'kleine-ordertoeslag';

  const COLLECTION_IDS = {
    MONIER_DAKPANNEN: '693404172675',
    MONIER_TOEBEHOREN: '693404434819',
    HULPSTUKKEN: '671027429763',
    ROOF_RENO: '695705895299',
    KINGSPAN_UNIDEK: '671028806019',
    KORAMIC_DAKPANNEN: '671029199235',
    FAKRO_DAKRAMEN: '671070552451',
    NEDSALE_DAKTOEBEHOREN: '695709991299',
    NELSKAMP: '699528544643'
  };

  const LOCATIE_RATES = { 2: 149, 3: 149, 4: 149, 5: 60, 6: 60, 7: 149 };
  const UNCATEGORIZED_RATE = 149;
  const TOEBEHOREN_FREE_THRESHOLD = 1000;
  const FAKRO_FREE_THRESHOLD = 350;
  const NEDSALE_FREE_THRESHOLD = 550;
  const DISCOUNT_MAX_QTY = 3;
  const MONIER_DAKPANNEN_FREE_QTY = 3000;
  const MAX_SHIPPING_COST = 358;
  const DEBUG = true;

  // Save native console.log before any widget can override it.
  const nativeLog = console.log.bind(console);

  // State management
  let isCalculating = false;
  let lastCategory = null;
  let isUpdating = false;
  let collectionCache = {};
  let confirmed = false;

  // ========== HELPER FUNCTIONS ==========

  function log(...args) {
    if (DEBUG) nativeLog('[ShipCalc]', ...args);
  }

  async function setCategory(category) {
    const value = category ? SHIPPING_CATEGORY_PREFIX + category : '';
    if (value === lastCategory) {
      fetch('/cart.js').then(r => r.json()).then(c => {
        const qty = (c.items || []).reduce((s, i) => s + i.quantity, 0);
        log('Category unchanged:', value, '| total items:', qty);
      }).catch(() => log('Category unchanged:', value));
      return;
    }

    isUpdating = true;
    lastCategory = value;

    try {
      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { shipping_category: value } })
      });
      if (res.ok) {
        confirmed = true;
        log('Set category:', category);
        document.dispatchEvent(new CustomEvent('shipping:category:updated', { detail: { category } }));
      }
    } catch (e) {
      log('Failed to set category:', e);
    } finally {
      isUpdating = false;
    }
  }

  /** Fast path: set DEFAULT on cart if it has items but no shipping_category. Does not touch lastCategory. */
  async function ensureDefault() {
    if (isUpdating || isCalculating) return;
    try {
      const res = await fetch('/cart.js');
      if (!res.ok) return;
      const cart = await res.json();
      if (!cart.items?.length || cart.attributes?.shipping_category) return;
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { shipping_category: SHIPPING_CATEGORY_PREFIX + 'DEFAULT' } })
      });
      confirmed = true;
      log('Ensure default: set DEFAULT (cart had no category)');
    } catch {}
  }

  async function getCollections(item) {
    // Try embedded data first (fastest)
    if (item.properties?._collection_ids) {
      return item.properties._collection_ids.split(',').map(id => id.trim());
    }
    if (item.product_collections) return item.product_collections.map(String);
    if (item.collections) return item.collections.map(String);

    // Fetch from product page (cached)
    if (!item.handle) return [];
    if (collectionCache[item.handle]) return collectionCache[item.handle];

    try {
      const res = await fetch(`/products/${item.handle}.js`);
      if (!res.ok) return [];
      const data = await res.json();
      const ids = (data.collections || []).map(c => c.id.toString());
      collectionCache[item.handle] = ids;
      return ids;
    } catch {
      return [];
    }
  }

  function determineCategory(flags, dakpannenQty, totalEuros, totalQty, locatie1Qty, fakroTotalCents, nedsaleTotalCents) {
    const { dakpannen, toebehoren, hulpstukken, roofReno, kingspan, koramic, fakroDakramen, nedsale, nelskamp, uncategorized } = flags;

    // Determine active locaties
    const locatieFlags = {
      1: dakpannen || toebehoren || hulpstukken,
      2: koramic, 3: roofReno, 4: kingspan,
      5: fakroDakramen, 6: nedsale, 7: nelskamp
    };
    const activeLocaties = [];
    for (const [num, active] of Object.entries(locatieFlags)) {
      if (active) activeLocaties.push(Number(num));
    }
    const hasCategorized = activeLocaties.length > 0;
    const isMultiLocatie = activeLocaties.length >= 2 || (activeLocaties.length >= 1 && uncategorized);

    // SINGLE LOCATIE path
    if (!isMultiLocatie) {
      // <3 products, ONLY Locatie 1 (Monier) → flat €60 shipping
      // Exception: dakpannen always ship at full rate, regardless of quantity
      if (totalQty < DISCOUNT_MAX_QTY && !dakpannen) {
        const isLocatie1Only = (toebehoren || hulpstukken) &&
          !roofReno && !kingspan && !koramic && !fakroDakramen && !nedsale && !nelskamp && !uncategorized;
        if (isLocatie1Only) return 'SHIPPING_60';
      }

      // 3000+ dakpannen single locatie → free
      if (dakpannenQty >= MONIER_DAKPANNEN_FREE_QTY) return 'SHIPPING_0';

      // Single category rules
      if (dakpannen) return 'SHIPPING_149';
      if (hulpstukken) return 'SHIPPING_149';
      if (toebehoren) return totalEuros >= TOEBEHOREN_FREE_THRESHOLD ? 'SHIPPING_0' : 'SHIPPING_60';
      if (roofReno) return 'SHIPPING_149';
      if (kingspan) return 'SHIPPING_149';
      if (koramic) return 'SHIPPING_149';
      if (fakroDakramen) return (fakroTotalCents / 100) >= FAKRO_FREE_THRESHOLD ? 'SHIPPING_0' : 'SHIPPING_60';
      if (nedsale) return (nedsaleTotalCents / 100) >= NEDSALE_FREE_THRESHOLD ? 'SHIPPING_0' : 'SHIPPING_60';
      if (nelskamp) return 'SHIPPING_149';
      if (uncategorized && !hasCategorized) return 'DEFAULT';
      return null;
    }

    // MULTI-LOCATIE: sum per-locatie costs
    let totalCost = 0;
    for (const locNum of activeLocaties) {
      if (locNum === 1) {
        // 3000+ dakpannen makes Locatie 1 free
        if (dakpannenQty >= MONIER_DAKPANNEN_FREE_QTY) {
          totalCost += 0;
        } else if (dakpannen) {
          // Dakpannen always ship at full rate — no <3 items discount
          totalCost += 149;
        } else {
          const l1Qty = locatie1Qty || 0;
          if (l1Qty < DISCOUNT_MAX_QTY) {
            totalCost += 60;
          } else if (toebehoren && !hulpstukken) {
            totalCost += totalEuros >= TOEBEHOREN_FREE_THRESHOLD ? 0 : 60;
          } else {
            totalCost += 149;
          }
        }
      } else if (locNum === 5) {
        totalCost += (fakroTotalCents / 100) >= FAKRO_FREE_THRESHOLD ? 0 : 60;
      } else if (locNum === 6) {
        totalCost += (nedsaleTotalCents / 100) >= NEDSALE_FREE_THRESHOLD ? 0 : 60;
      } else {
        totalCost += LOCATIE_RATES[locNum] || UNCATEGORIZED_RATE;
      }
    }
    if (uncategorized) totalCost += UNCATEGORIZED_RATE;

    // Cap at maximum
    totalCost = Math.min(totalCost, MAX_SHIPPING_COST);

    return 'SHIPPING_' + totalCost;
  }

  async function calculate() {
    if (isUpdating || isCalculating) return;
    isCalculating = true;

    try {
      const res = await fetch('/cart.js');
      if (!res.ok) throw new Error('Cart fetch failed');
      const cart = await res.json();
      const items = (cart.items || []).filter(item => item.handle !== SURCHARGE_HANDLE);

      if (!items.length) {
        isCalculating = false;
        return;
      }

      // Harden default: patch missing attribute before any async work that might fail
      if (!cart.attributes?.shipping_category) {
        await setCategory('DEFAULT');
      }

      // Categorize items — fetch all collections in parallel
      const flags = {
        dakpannen: false, toebehoren: false, hulpstukken: false,
        roofReno: false, kingspan: false, koramic: false,
        fakroDakramen: false, nedsale: false, nelskamp: false, uncategorized: false
      };
      let dakpannenQty = 0;
      let locatie1Qty = 0;
      let totalQty = 0;
      let total = 0;
      let fakroTotalCents = 0;
      let nedsaleTotalCents = 0;

      const allCols = await Promise.all(items.map(item => getCollections(item)));

      for (let i = 0; i < items.length; i++) {
        const cols = allCols[i];
        const item = items[i];
        let found = false;

        if (cols.includes(COLLECTION_IDS.MONIER_DAKPANNEN)) { flags.dakpannen = true; dakpannenQty += item.quantity; locatie1Qty += item.quantity; found = true; }
        if (cols.includes(COLLECTION_IDS.MONIER_TOEBEHOREN)) { flags.toebehoren = true; locatie1Qty += item.quantity; found = true; }
        if (cols.includes(COLLECTION_IDS.HULPSTUKKEN)) { flags.hulpstukken = true; locatie1Qty += item.quantity; found = true; }
        if (cols.includes(COLLECTION_IDS.ROOF_RENO)) { flags.roofReno = true; found = true; }
        if (cols.includes(COLLECTION_IDS.KINGSPAN_UNIDEK)) { flags.kingspan = true; found = true; }
        if (cols.includes(COLLECTION_IDS.KORAMIC_DAKPANNEN)) { flags.koramic = true; found = true; }
        if (cols.includes(COLLECTION_IDS.FAKRO_DAKRAMEN)) { flags.fakroDakramen = true; fakroTotalCents += item.final_line_price || item.line_price || 0; found = true; }
        if (cols.includes(COLLECTION_IDS.NEDSALE_DAKTOEBEHOREN)) { flags.nedsale = true; nedsaleTotalCents += item.final_line_price || item.line_price || 0; found = true; }
        if (cols.includes(COLLECTION_IDS.NELSKAMP)) { flags.nelskamp = true; found = true; }
        if (!found) flags.uncategorized = true;

        totalQty += item.quantity;
        total += item.final_line_price || item.line_price || 0;
      }

      const category = determineCategory(flags, dakpannenQty, total / 100, totalQty, locatie1Qty, fakroTotalCents, nedsaleTotalCents);
      await setCategory(category || 'DEFAULT');

    } catch (e) {
      log('Calculation error:', e);
      await setCategory('DEFAULT');
    } finally {
      isCalculating = false;
    }
  }

  // ========== INITIALIZATION ==========

  function init() {
    log('Initialized');

    // Harden default: ensure DEFAULT immediately, then run full calculation sooner
    (async () => {
      try {
        await ensureDefault();
        const res = await fetch('/cart.js');
        if (res.ok) {
          const cart = await res.json();
          if (cart.attributes?.shipping_category) {
            confirmed = true;
            lastCategory = cart.attributes.shipping_category;
          }
        }
      } catch {
        try {
          await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ attributes: { shipping_category: SHIPPING_CATEGORY_PREFIX + 'DEFAULT' } })
          });
        } catch {}
      }
      setTimeout(calculate, 100);
    })();

    // Debounced recalculation
    let timeout;
    function recalc(delay = 300) {
      confirmed = false;
      clearTimeout(timeout);
      timeout = setTimeout(calculate, delay);
    }

    // Cart events: set default immediately, then recalc
    ['cart:updated', 'cart:add', 'product:add', 'on:cart:add', 'product:added-to-cart',
     'cart:item:removed', 'cart:remove', 'on:line-item:change'].forEach(evt => {
      document.addEventListener(evt, () => {
        ensureDefault();
        recalc();
      });
    });

    // Form submissions (add to cart)
    document.addEventListener('submit', e => {
      if (e.target.action?.includes('/cart/add')) {
        ensureDefault();
        recalc();
      }
    });

    // Intercept fetch for cart changes: recalculate immediately after cart mutation completes
    const origFetch = window.fetch;
    window.fetch = function(input, ...rest) {
      const url = typeof input === 'string' ? input : (input instanceof Request ? input.url : '');
      if (url.includes('/cart/add') || url.includes('/cart/change')) {
        if (!isUpdating) {
          ensureDefault();
          const p = origFetch.call(this, input, ...rest);
          p.then(() => {
            calculate(); // no debounce — recalculate immediately after cart change
          }).catch(() => {});
          return p;
        }
      }
      return origFetch.call(this, input, ...rest);
    };

    // Quantity/remove changes
    document.addEventListener('change', e => {
      if (e.target.matches('input[name*="updates"]')) {
        ensureDefault();
        recalc(200);
      }
    });
    document.addEventListener('click', e => {
      if (e.target.closest('[data-cart-remove], .cart-remove, .cart__remove, [data-cart-item-remove]')) {
        ensureDefault();
        recalc();
      }
    });

    // Guard checkout: if a calculation is in-flight, wait for it before navigating
    document.addEventListener('click', e => {
      const link = e.target.closest('a[href*="/checkout"], [name="checkout"], button[name="checkout"]');
      if (!link) return;
      if (!isCalculating && !isUpdating) return; // calculation done, let them through

      e.preventDefault();
      e.stopImmediatePropagation();
      log('Checkout blocked — waiting for shipping calculation');

      const poll = setInterval(() => {
        if (!isCalculating && !isUpdating) {
          clearInterval(poll);
          link.click();
        }
      }, 50);
      // Safety: release after 3s no matter what
      setTimeout(() => clearInterval(poll), 3000);
    }, true); // capture phase to run before other handlers

    // Tab visible again: cart may have been changed elsewhere or attribute lost
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') ensureDefault();
    });

    // Beacon fallback on unload
    window.addEventListener('beforeunload', () => {
      if (!confirmed) {
        try {
          const blob = new Blob([JSON.stringify({ attributes: { shipping_category: SHIPPING_CATEGORY_PREFIX + 'DEFAULT' } })], { type: 'application/json' });
          navigator.sendBeacon('/cart/update.js', blob);
        } catch {}
      }
    });

    // Periodic check - ensure DEFAULT exists, then run full calculation (every 10s)
    setInterval(async () => {
      if (isCalculating || isUpdating) return;
      try {
        await ensureDefault();
        calculate();
      } catch {}
    }, 10000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
